import React from "react";
import {
  getFlowAnalysis,
  saveBranchProbabilities,
} from "../../services/flowAnalysisService";

function fmtNum(x) {
  return Number(x || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
}
function minsToHM(min) {
  const total = Math.max(0, Math.round(Number(min) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h} h ${m} m` : `${m} m`;
}

const tile = (accent) => ({
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderTop: `3px solid ${accent}`,
  borderRadius: 10,
  padding: "12px 14px",
  minWidth: 150,
  flex: "1 1 150px",
});
const tileLabel = { fontSize: 11, color: "var(--gray-500)", fontWeight: 600 };
const tileValue = { fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1.2 };

const inputStyle = {
  width: 90,
  padding: "6px 8px",
  border: "1px solid var(--gray-300)",
  borderRadius: 6,
  fontSize: 13,
  boxSizing: "border-box",
};

/**
 * Flow-analysis panel (FBPM Ch.7): cycle time, value-adding time, Cycle-Time
 * Efficiency (CTE), labor cost, and — with branch probabilities — expected cost.
 * readOnly=true → View (metrics + probabilities read-only); false → Edit (editable).
 */
export default function FlowAnalysisPanel({ processId, isActive, readOnly = false }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState("");
  const [savedMsg, setSavedMsg] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [probs, setProbs] = React.useState({});
  const originalRef = React.useRef({});
  // Knock-out inputs (FBPM Ch.8): share of cases each check rejects via a branch.
  const [rejects, setRejects] = React.useState({});
  const originalRejectsRef = React.useRef({});
  // AI-suggested probabilities (propose-and-confirm): keyed by branch_pk_id.
  // Only EMPTY inputs are prefilled — a value the analyst set always wins.
  const [aiSuggested, setAiSuggested] = React.useState({});
  const [aiMsg, setAiMsg] = React.useState("");
  const [suggesting, setSuggesting] = React.useState(false);

  const handleSuggest = React.useCallback(async () => {
    setSuggesting(true);
    setAiMsg("");
    try {
      const { suggestBranchProbabilities } = await import("../../services/processOptimizationService");
      const res = await suggestBranchProbabilities(processId);
      const status = res?.llm_status?.status;
      const rows = Array.isArray(res?.suggestions) ? res.suggestions : [];
      if (status === "failed") {
        setAiMsg(`⚠ AI suggestion failed: ${res?.llm_status?.detail || "unknown error"}`);
        return;
      }
      if (!rows.length) {
        setAiMsg(res?.llm_status?.detail || "Nothing to suggest — every branch already has a probability.");
        return;
      }
      const byId = {};
      rows.forEach((s) => { byId[s.branch_pk_id] = s; });
      setAiSuggested(byId);
      const nextProbs = {};
      setProbs((prev) => {
        const next = { ...prev };
        rows.forEach((s) => {
          // User precedence: never replace a value that is already filled in.
          if ((next[s.branch_pk_id] ?? "") === "") next[s.branch_pk_id] = String(s.suggested_probability);
          nextProbs[s.branch_pk_id] = next[s.branch_pk_id];
        });
        return next;
      });
      setRejects((prev) => {
        const next = { ...prev };
        rows.forEach((s) => {
          // User precedence: if right-side (reject) field is empty, automatically fill by subtracting left-side value from 1
          if ((next[s.branch_pk_id] ?? "") === "") {
            const probVal = nextProbs[s.branch_pk_id] ?? String(s.suggested_probability);
            const prob = Number(probVal);
            if (!isNaN(prob) && prob >= 0 && prob <= 1) {
              const rejectVal = Math.round((1 - prob) * 10000) / 10000;
              next[s.branch_pk_id] = String(rejectVal);
            }
          }
        });
        return next;
      });
      setAiMsg(`${rows.length} AI estimate(s) prefilled — review each (hover for the reasoning), adjust if needed, then Save.`);
    } catch (e) {
      setAiMsg(`⚠ AI suggestion failed: ${e?.message || "unknown error"}`);
    } finally {
      setSuggesting(false);
    }
  }, [processId]);

  const load = React.useCallback(async () => {
    if (!processId) return;
    setLoading(true);
    setError("");
    try {
      const res = await getFlowAnalysis(processId);
      setData(res);
      const seed = {};
      (res.branches || []).forEach((b) => {
        seed[b.branch_pk_id] = b.probability == null ? "" : String(b.probability);
      });
      setProbs(seed);
      const rseed = {};
      (res.branches || []).forEach((b) => {
        rseed[b.branch_pk_id] = b.reject_rate == null ? "" : String(b.reject_rate);
      });
      setRejects(rseed);
      originalRef.current = { ...seed };
      originalRejectsRef.current = { ...rseed };
      setLoaded(true);
    } catch (e) {
      setError(e.message || "Failed to load flow analysis");
    } finally {
      setLoading(false);
    }
  }, [processId]);

  React.useEffect(() => {
    const shouldLoad = isActive === undefined ? true : isActive;
    if (shouldLoad && processId && !loaded && !loading) load();
  }, [isActive, processId, loaded, loading, load]);

  const handleSave = async () => {
    setSaving(true);
    setSavedMsg("");
    setError("");
    try {
      const orig = originalRef.current;
      const origR = originalRejectsRef.current;
      const changed = new Map();
      Object.entries(probs).forEach(([id, v]) => {
        if ((v || "") !== (orig[id] || "")) changed.set(id, { branch_pk_id: Number(id), probability: v === "" ? null : Number(v) });
      });
      Object.entries(rejects).forEach(([id, v]) => {
        if ((v || "") !== (origR[id] || "")) {
          const row = changed.get(id) || { branch_pk_id: Number(id) };
          row.reject_rate = v === "" ? null : Number(v);
          changed.set(id, row);
        }
      });
      const items = [...changed.values()];
      if (items.length === 0) {
        setSavedMsg("No changes to save.");
      } else {
        await saveBranchProbabilities(processId, items);
        setSavedMsg("Probabilities saved.");
        setLoaded(false);
        await load();
      }
    } catch (e) {
      setError(e.message || "Failed to save probabilities");
    } finally {
      setSaving(false);
    }
  };

  const dq = (data && data.data_quality) || {};
  const cte = data && data.cycle_time_efficiency_percent;

  return (
    <div style={{ padding: "2px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Flow Analysis</h3>
        <span style={{ fontSize: 12, color: "var(--gray-500)" }}>
          Cycle time, cycle-time efficiency (CTE) and cost across the process
        </span>
      </div>

      {loading && <p style={{ color: "var(--gray-500)" }}>Loading…</p>}
      {error && (
        <div style={{ margin: "10px 0", padding: "8px 12px", background: "var(--red-tint-50)", border: "1px solid var(--red-tint-200)", borderRadius: 8, color: "var(--red-ink-700)", fontSize: 13 }}>
          {error}
        </div>
      )}

      {data && (
        <>
          <div style={{ margin: "6px 0 8px", fontSize: 12, color: "var(--gray-500)" }}>
            First-cut flow analysis: task times summed along the sequence; XOR/inclusive
            branch probabilities weight the expected cost. Times in minutes.
          </div>

          {/* Metric tiles */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "6px 0 14px" }}>
            <div style={tile("#2b79c2")}>
              <div style={tileLabel}>Cycle time</div>
              <div style={tileValue}>{fmtNum(data.cycle_time_minutes)} min</div>
              <div style={{ fontSize: 11, color: "var(--gray-400)" }}>≈ {minsToHM(data.cycle_time_minutes)}</div>
              <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 2 }}>
                proc {fmtNum(data.processing_time_minutes)} · wait {fmtNum(data.waiting_time_minutes)} · rework {fmtNum(data.rework_time_minutes)}
              </div>
            </div>
            <div style={tile("#7c55c6")}>
              <div style={tileLabel}>Cycle-time efficiency</div>
              <div style={{ ...tileValue, color: cte == null ? "var(--gray-400)" : "#7c55c6" }}>
                {cte == null ? "—" : `${cte}%`}
              </div>
              <div style={{ fontSize: 11, color: "var(--gray-400)" }}>value-adding ÷ cycle time</div>
              {cte != null && (
                <div style={{ marginTop: 6, height: 6, background: "var(--gray-100)", borderRadius: 999, overflow: "hidden" }}>
                  <div style={{ width: `${Math.min(100, cte)}%`, height: "100%", background: "#7c55c6" }} />
                </div>
              )}
            </div>
            <div style={tile("#2e8b63")}>
              <div style={tileLabel}>Value-adding time</div>
              <div style={tileValue}>{fmtNum(data.value_adding_time_minutes)} min</div>
              <div style={{ fontSize: 11, color: "var(--gray-400)" }}>from VA-classified steps</div>
            </div>
            <div style={tile("#a96f18")}>
              <div style={tileLabel}>Labor cost / case</div>
              <div style={tileValue}>{fmtNum(data.labor_cost_per_case)}</div>
              <div style={{ fontSize: 11, color: "var(--gray-400)" }}>expected: {fmtNum(data.expected_labor_cost_per_case)}</div>
            </div>
          </div>

          {(data.value_adding_time_minutes === 0 || dq.tasks_missing_classification > 0) && (
            <div style={{ margin: "0 0 14px", fontSize: 12.5, color: "#7c55c6", background: "var(--violet-tint-100)", border: "1px solid #7c55c622", borderRadius: 8, padding: "8px 12px" }}>
              CTE needs steps marked <b>VA</b> in the Value-Added tab.
              {dq.tasks_missing_classification > 0 ? ` ${dq.tasks_missing_classification} step(s) are still unclassified.` : ""}
            </div>
          )}

          {/* Branch probabilities */}
          {(data.branches || []).length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, margin: "8px 0 6px" }}>
                Branch probabilities <span style={{ fontWeight: 400, color: "var(--gray-400)", fontSize: 12 }}>(XOR / inclusive splits — weight the expected cost)</span>
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {(data.branches || []).map((b) => (
                  <div key={b.branch_pk_id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", border: "1px solid var(--gray-200)", borderRadius: 8, padding: "8px 10px", background: "var(--color-surface)" }}>
                    <span style={{ fontSize: 12.5, flex: 1, minWidth: 160 }}>
                      <b>{b.gateway_name || b.gateway_type}</b>
                      {b.condition ? <span style={{ color: "var(--gray-500)" }}> — {b.condition}</span> : null}
                      {b.target_task_name ? <span style={{ color: "var(--gray-400)" }}> → {b.target_task_name}</span> : null}
                    </span>
                    {readOnly ? (
                      <span style={{ fontSize: 13, fontWeight: 600, color: b.probability == null ? "var(--gray-400)" : "var(--gray-900)" }}>
                        {b.probability == null ? "—" : Number(b.probability).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </span>
                    ) : (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        {aiSuggested[b.branch_pk_id] && String(probs[b.branch_pk_id]) === String(aiSuggested[b.branch_pk_id].suggested_probability) && (
                          <span
                            title={`AI estimate — ${aiSuggested[b.branch_pk_id].rationale || "review before saving"}`}
                            style={{ fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 999, border: "1px solid var(--amber-tint-200)", background: "var(--amber-tint-50)", color: "var(--amber-ink-700)", cursor: "help" }}
                          >
                            AI
                          </span>
                        )}
                        <input
                          style={inputStyle}
                          type="number"
                          min="0"
                          max="1"
                          step="0.05"
                          placeholder="0–1"
                          value={probs[b.branch_pk_id] ?? ""}
                          onChange={(e) => setProbs((prev) => ({ ...prev, [b.branch_pk_id]: e.target.value }))}
                          aria-label="Branch probability (0 to 1)"
                        />
                        <label title="Of the cases reaching this check, the share REJECTED via this branch (0..1). Unlocks the knock-out check-order analysis (Flow & Bottlenecks tab)." style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, fontWeight: 700, color: "var(--orange-ink-800)" }}>
                          reject
                          <input
                            style={{ ...inputStyle, width: 64, borderColor: "var(--orange-tint-300)" }}
                            type="number"
                            min="0"
                            max="1"
                            step="0.05"
                            placeholder="0–1"
                            value={rejects[b.branch_pk_id] ?? ""}
                            onChange={(e) => setRejects((prev) => ({ ...prev, [b.branch_pk_id]: e.target.value }))}
                            aria-label="Reject rate of this branch (0 to 1) — knock-out input"
                          />
                        </label>
                      </span>
                    )}
                  </div>
                ))}
              </div>
              {!readOnly && (
                <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{ padding: "9px 20px", background: saving ? "var(--accent-tint-300)" : "var(--accent-600)", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: saving ? "default" : "pointer" }}
                  >
                    {saving ? "Saving…" : "Save probabilities"}
                  </button>
                  <button
                    onClick={handleSuggest}
                    disabled={suggesting}
                    title="The AI proposes probabilities ONLY for branches you left empty (from the condition labels and target activities). Nothing is saved until you review and press Save — your own values are never replaced."
                    style={{ padding: "9px 16px", background: "var(--color-surface)", color: "var(--amber-ink-700)", border: "1.5px solid #f59e0b", borderRadius: 8, fontWeight: 600, cursor: suggesting ? "default" : "pointer" }}
                  >
                    {suggesting ? "Asking AI…" : "Suggest missing (AI)"}
                  </button>
                  {savedMsg && <span style={{ color: "var(--emerald-ink-600)", fontSize: 13 }}>{savedMsg}</span>}
                  {aiMsg && <span style={{ color: aiMsg.startsWith("⚠") ? "var(--red-ink-700)" : "var(--amber-ink-700)", fontSize: 12.5 }}>{aiMsg}</span>}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
